import moment from 'moment';
import {useCallback, useMemo, useState} from 'react';
import {IntlShape} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {selectIdOnEdit} from '../../../../../features/modals';
import {
    deleteWorkCalendar,
    getMonth,
    getWorkCalendars,
    setCurrentYear,
    setWorkCalendarId
} from '../../../../../features/settings/workcalendar';
import {IdNameModel, WorkCalendar as WorkCalendarObj, Year} from '../../../../../features/settings/workcalendar/types';
import { canManageWorkCalendars } from '../../../../../features/profile/entityPermissions';
import {useAppDispatch, useAppSelector} from '../../../../../hooks/hooks';
import {Lx} from '../../../../../i18n/consts';
import {NAVIGATION_ROUTES} from '../../../../../routes/NavigationRoute';
import {ViewCalendar} from '../types';

type Args = {
    currentYear: number;
    intl: IntlShape;
    workCalendarId: number;
    workCalendars: WorkCalendarObj[];
    year: Year;
};

const useWorkCalendarActions = ({
    currentYear,
    intl,
    workCalendarId,
    workCalendars,
    year
}: Args) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const profile = useAppSelector((state) => state.profile);
    const canEditWorkCalendars = canManageWorkCalendars(profile);
    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [calendarToDelete, setCalendarToDelete] = useState<WorkCalendarObj | null>(null);

    const selectedCalendar = useMemo(
        () => workCalendars.find((calendar) => calendar.id === workCalendarId) ?? null,
        [workCalendarId, workCalendars]
    );

    const deleteMessage = useMemo(() => {
        if (!calendarToDelete) {
            return '';
        }

        const baseMessage = intl.formatMessage(
            { id: Lx.Calendar.DELETE_CALENDAR_DESC },
            { s: calendarToDelete.name ?? '' }
        );

        return calendarToDelete.isRoot
            ? `${baseMessage} ${intl.formatMessage({ id: Lx.Calendar.DELETE_CALENDAR_ROOT_WARNING })}`
            : baseMessage;
    }, [calendarToDelete, intl]);

    const handleCalendarSelect = useCallback((value?: ViewCalendar) => {
        if (!value) {
            const model: IdNameModel = {
                id: workCalendarId,
                name: ''
            };
            dispatch(selectIdOnEdit(model));
            return;
        }

        dispatch(setWorkCalendarId(value.calendar));
        dispatch(setCurrentYear(value.year));
    }, [dispatch, workCalendarId]);

    const handleDayClick = useCallback(async (date: moment.Moment) => {
        if (!canEditWorkCalendars || !year) {
            return;
        }

        const month = date.month() + 1;
        const dayNumber = date.date();
        const fallbackDay = year.months.find((item) => item.month === month)?.days.find((item) => item.day === dayNumber);

        try {
            const monthData = await dispatch(getMonth({ workCalendarId, year: currentYear, month })).unwrap();
            const updatedDay = monthData.days.find((item) => item.day === dayNumber) ?? fallbackDay;

            if (!updatedDay) {
                return;
            }

            navigate(NAVIGATION_ROUTES.SETTINGS_CALENDAR_DAY_EDIT, {
                state: {
                    workCalendarId,
                    year: currentYear,
                    month,
                    day: updatedDay,
                },
            });
        } catch (error) {
            console.error(error);
        }
    }, [canEditWorkCalendars, currentYear, dispatch, navigate, workCalendarId, year]);

    const handleCalendarEdit = useCallback(() => {
        if (!canEditWorkCalendars || !selectedCalendar) {
            return;
        }

        navigate(NAVIGATION_ROUTES.SETTINGS_CALENDAR_EDIT, { state: { calendar: selectedCalendar } });
    }, [canEditWorkCalendars, navigate, selectedCalendar]);

    const handleCalendarCreate = useCallback(() => {
        if (!canEditWorkCalendars) {
            return;
        }

        navigate(NAVIGATION_ROUTES.SETTINGS_CALENDAR_CREATE);
    }, [canEditWorkCalendars, navigate]);

    const handleCalendarDelete = useCallback(() => {
        if (!canEditWorkCalendars || !selectedCalendar) {
            return;
        }

        setCalendarToDelete(selectedCalendar);
        setDeleteDialogOpen(true);
    }, [canEditWorkCalendars, selectedCalendar]);

    const handleDeleteCancel = useCallback(() => {
        setDeleteDialogOpen(false);
        setCalendarToDelete(null);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!canEditWorkCalendars || !calendarToDelete) {
            return;
        }

        await dispatch(deleteWorkCalendar(calendarToDelete.id)).unwrap();
        setDeleteDialogOpen(false);
        setCalendarToDelete(null);
        dispatch(getWorkCalendars());
    }, [calendarToDelete, canEditWorkCalendars, dispatch]);

    return {
        deleteMessage,
        isDeleteDialogOpen,
        handleCalendarCreate,
        handleCalendarDelete,
        handleCalendarEdit,
        handleCalendarSelect,
        handleDayClick,
        handleDeleteCancel,
        handleDeleteConfirm,
    };
};

export default useWorkCalendarActions;
